/**
 * members.cooling_alert — relationships going cold (FB-J3V15AT5).
 *
 * Top hot/warm relationships that haven't been touched in a while.
 * "Hot >60d silent" or "warm >90d silent" — the cards that need
 * outreach this week. Pulls from /top-relationships and filters
 * client-side.
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { WidgetProps } from "../registry";

interface Row {
  bioguide_id: string;
  display_name?: string;
  party?: string;
  state?: string;
  district?: number | null;
  relationship_tier: "hot" | "warm" | "cool" | "cold" | "never_engaged";
  relationship_score: number;
  last_touch_at: string | null;
  touch_count: number;
}

function daysSince(d: string | null): number | null {
  if (!d) return null;
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
}

export function MembersCoolingAlert({ spec, workspaceId }: WidgetProps) {
  const limit = (spec.params?.limit as number) ?? 8;
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "members.cooling", workspaceId, limit],
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/workspaces/${workspaceId}/top-relationships?limit=200`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      return (json.members ?? []) as Row[];
    },
  });

  if (error) return <DataUnavailable message="Relationship alerts could not be loaded." retry={refetch} />;
  if (isLoading) return <div role="status" className="space-y-1.5"><span className="sr-only">Loading…</span><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-3/4" /><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-1/2" /><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-2/3" /></div>;

  const cooling = (data ?? [])
    .filter((m) => {
      const ds = daysSince(m.last_touch_at);
      if (ds === null) return false;
      if (m.relationship_tier === "hot" && ds > 60) return true;
      if (m.relationship_tier === "warm" && ds > 90) return true;
      return false;
    })
    .sort((a, b) => (daysSince(b.last_touch_at) ?? 0) - (daysSince(a.last_touch_at) ?? 0))
    .slice(0, limit);

  if (cooling.length === 0) {
    return <div className="flex items-center gap-1.5 text-stone-600 text-xs"><CheckCircle2 size={13} aria-hidden="true" className="text-emerald-700" />All hot/warm relationships are fresh.</div>;
  }
  return (
    <ul className="space-y-1.5 text-xs">
      {cooling.map((m) => {
        const ds = daysSince(m.last_touch_at);
        const dot = m.relationship_tier === "hot" ? "bg-red-500" : "bg-orange-500";
        return (
          <li key={m.bioguide_id}>
            <Link
              to={`/members/${encodeURIComponent(m.bioguide_id)}`}
              className="flex items-center gap-2 px-2 py-1 bg-stone-50 hover:bg-amber-50 rounded transition-colors"
            >
              <span className={`w-2 h-2 rounded-full ${dot}`} title={m.relationship_tier} />
              <span className="flex-1 text-ink truncate">{m.display_name ?? m.bioguide_id}</span>
              <span className="text-stone-600 text-[10px] font-mono">{m.state}{m.district ? `-${m.district}` : ""}</span>
              <span className="text-amber-800 text-[10px] font-semibold whitespace-nowrap">{ds}d cold</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
